// The one shape a client-side realtime catch-up failure is logged in — BUG-1306.
//
// Every store in this directory swallows its catch-up failures on purpose
// (best-effort; the next trigger retries), which makes the log line the ONLY
// signal that anything went wrong. Before this module there were five hand-built
// payloads and they all made the same mistake: nesting the caught `Error` inside
// the logged context object. An `Error` has no own enumerable properties, so
// every serializer between the catch and a reader — JSON log forwarding,
// structured clone, Next.js's browser→terminal forwarding — renders it `{}`. The
// context survived; the reason did not, and a store stuck in a failing loop was
// indistinguishable from a quiet, healthy one.
//
// This exists as an argument BUILDER rather than a pair of field helpers so the
// mistake cannot be made half-way: a call site either adopts the whole shape or
// none of it. That is what makes it a real choke point rather than a convention.
//
// `console.error`, NOT `createLogger` (ADR 005 / rule #13): these are browser
// surfaces and `createLogger` is server-only (ALS / `node:async_hooks`).

/**
 * Extract an HTTP status from a thrown catch-up error, if it carries one.
 *
 * `SnapshotCatchupError` (the default snapshot fetcher) sets it; a custom
 * fetcher may attach a numeric `status` too. A plain network reject (the
 * `TypeError` `fetch` throws when offline / DNS fails) has none → `undefined`,
 * which is what lets a caller tell an HTTP failure apart from a network drop.
 *
 * @param err - The value caught from a rejected catch-up.
 * @returns The numeric HTTP status, or `undefined` when the error carries none.
 */
export function httpStatusOf(err: unknown): number | undefined {
    if (err && typeof err === 'object' && 'status' in err) {
        const status = (err as { status: unknown }).status;
        if (typeof status === 'number') return status;
    }
    return undefined;
}

/**
 * Build the `console.error` arguments for a realtime catch-up failure.
 *
 * The returned context carries the cause as a plain `error` STRING (and a
 * numeric `status` when the failure had one), so it survives serialization. The
 * `Error` instance rides as a third positional argument, which the browser
 * console renders with its stack rather than flattening — the shape the two
 * already-compliant sibling sites in the repo use.
 *
 * Spread it straight into the call:
 * `console.error(...formatRealtimeFailure('[X] reconcile failed', { topic }, err))`
 *
 * Callers logging a `topic` pass it through `redactTopicSecret` first — an
 * `admin:` topic ends in an HMAC that gates channel access, so the raw string is
 * a bearer credential rather than a name.
 *
 * @param message - The log line's leading message, e.g. `'[X] reconcile failed'`.
 * @param context - Serializable identifying fields (topic, auction id, event).
 * @param err - The value caught from the rejected catch-up.
 * @returns The `console.error` argument tuple: message, context + cause, Error.
 */
export function formatRealtimeFailure(
    message: string,
    context: Record<string, unknown>,
    err: unknown,
): [string, Record<string, unknown>, Error] {
    const cause = err instanceof Error ? err : new Error(String(err));
    const status = httpStatusOf(err);
    return [
        message,
        {
            ...context,
            error: cause.message,
            ...(status === undefined ? {} : { status }),
        },
        cause,
    ];
}
